import { useEffect, useMemo, useRef, useState } from 'react'
import { loadStripe, type StripeError } from '@stripe/stripe-js'
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'

/**
 * decline_code / code を顧客向けの「次のアクション」を示すメッセージに変換する。
 *
 * 設計方針:
 * - Radar/不正検知系 (fraudulent / lost_card / stolen_card 等) は **同一の
 *   "セキュリティ上の理由"** メッセージに集約。具体閾値や「盗難届」の事実を
 *   出さず、攻撃者の学習を防ぎつつ正規顧客には次のアクション (時間をおく /
 *   別カード) を伝える。
 * - 顧客が修正可能なエラー (CVC / ZIP / 期限切れ) は具体的にどこを直すか案内。
 * - 不明なエラーは fallback で「別カード or 時間をおいて再試行」を促す。
 */
function mapStripeError(error: StripeError, t: (k: string) => string): string {
  const decline = error.decline_code
  const code = error.code

  // セキュリティ判定で拒否されたパターン (Radar / 発行会社のリスク判定)。
  // lost_card / stolen_card は事実を伝えると犯人が他カード試行に移る合図に
  // なるので、意図的に generic 化する (Stripe 公式も同方針)。
  if (
    decline === 'fraudulent' ||
    decline === 'generic_decline' ||
    decline === 'do_not_honor' ||
    decline === 'lost_card' ||
    decline === 'stolen_card' ||
    decline === 'pickup_card' ||
    decline === 'restricted_card'
  ) {
    return t('stripe.error.security')
  }
  if (decline === 'card_velocity_exceeded' || code === 'card_velocity_exceeded') {
    return t('stripe.error.velocity')
  }
  if (decline === 'insufficient_funds') {
    return t('stripe.error.insufficientFunds')
  }
  if (decline === 'expired_card' || code === 'expired_card') {
    return t('stripe.error.expiredCard')
  }
  if (decline === 'incorrect_cvc' || code === 'incorrect_cvc') {
    return t('stripe.error.incorrectCvc')
  }
  if (decline === 'incorrect_zip' || code === 'incorrect_zip') {
    return t('stripe.error.incorrectZip')
  }
  // 上記いずれにも該当しない (= 大半の card_error / processing_error 等)。
  // Stripe SDK が返す localized message は短文で次のアクションが無いため、
  // "別カード or 時間をおいて" を促す自前の fallback に置換する。
  return t('stripe.error.fallback')
}

/**
 * 与信 (manual capture) では confirm 後の PI は `succeeded` ではなく
 * `requires_capture` で止まる。**ここを取り違えると、成立している決済を
 * 「障害」と表示して客に再試行させる**ことになる (体験チケットで実際に発生)。
 */
function okStatusesFor(captureMethod?: 'automatic' | 'manual'): string[] {
  return captureMethod === 'manual' ? ['requires_capture', 'succeeded'] : ['succeeded']
}

/**
 * 既に確定済みの PaymentIntent を再度 confirm すると Stripe は
 * `payment_intent_unexpected_state` を返す。二度押しや「失敗と誤表示されて
 * 客が再試行した」場合がこれで、**実際には支払いは成立している**。
 * エラーに載っている PI の状態を見て、成立済みなら成功として扱う。
 */
function alreadyConfirmedIntentId(
  error: StripeError, captureMethod?: 'automatic' | 'manual',
): string | null {
  const pi = error.payment_intent
  if (!pi) return null
  return okStatusesFor(captureMethod).includes(pi.status) ? pi.id : null
}

/**
 * #2190 項目 7: `processing` は失敗ではない (後から成立しうる)。以前は「もう一度お試しください」と出して
 * 払い直しを誘い、1 回目が後から成立すると二重請求になった。払い直させずに状態を見続ける
 * (pages/ChargeReturn.tsx と同じ見方)。見続ける間隔と長さ:
 */
const PROCESSING_POLL_MS = 3000
const PROCESSING_WATCH_MS = 120_000

interface StripeCheckoutProps {
  clientSecret: string
  amount: number
  storeName: string
  connectedAccountId?: string | null
  /**
   * #625: card-daily / card-rate を charge 前にチェックするのに必要な情報。
   * 設定すると StripeCheckout は createPaymentMethod → /precheck-payment →
   * confirmPayment の 3 段階フローを取り、違反時は charge せず 400 を表示する。
   * 未設定の場合は legacy の confirmPayment(elements) 1 発フローで動作し、
   * 違反検知は backend の post-charge fallback (= 自動 refund) に委ねる。
   */
  /**
   * 与信 (manual capture) の文言をどちらで出すか。出前は「調理を開始した瞬間」、
   * 体験は「当日ご利用時」で capture のタイミングが違うため、使い回すと嘘になる。
   * 既定は出前 (先に実装された側) 。
   */
  // #2190 'reservation' = 予約の与信 (お店の承認などで請求。出前の「調理を開始した瞬間」ではない)
  holdKind?: 'delivery' | 'experience' | 'reservation'
  paymentIntentId?: string
  storeId?: string
  /**
   * When 'manual' (delivery pre-auth), a confirmed PaymentIntent lands in
   * `requires_capture`, not `succeeded`. Button copy also switches to
   * "仮押さえ" wording so users know no money has moved yet.
   */
  captureMethod?: 'automatic' | 'manual'
  onSuccess: (paymentIntentId: string) => void
  onCancel: () => void
  /** #2190 項目 7: `processing` を見ている間 true。呼び元は閉じさせない (閉じると見張りが止まる) */
  onBusyChange?: (busy: boolean) => void
  /** #2577 予約の仮押さえの期限 (ISO)。過ぎたら払わせない (巡回が決済を取り消して予約を期限切れにする) */
  payBy?: string | null
  /**
   * #2577 仮押さえの期限を過ぎたときに呼ぶ。お店がもう承認した予約 (仮押さえは関係ない) なら true を返し、払ってよい。
   * 無ければ期限で止める
   */
  canPayAfterHold?: () => Promise<boolean>
  /**
   * #2577 予約の決済が取り消されていた (仮押さえ切れ・ほかのタブでポイントで払った・お店が先に承認した等)。
   * 呼び元がサーバに確かめ、片付けたら (完了にした・決済を作り直した) null、そうでなければ出す文言を返す
   */
  onIntentCanceled?: () => Promise<string | null>
}

function CheckoutForm({
  clientSecret,
  amount,
  storeName,
  paymentIntentId,
  storeId,
  captureMethod,
  holdKind,
  onSuccess,
  onCancel,
  onBusyChange,
  payBy,
  canPayAfterHold,
  onIntentCanceled,
}: Omit<StripeCheckoutProps, 'connectedAccountId'>) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useI18n()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // #978 Apple Pay/Google Pay が使える端末でのみ Express Checkout ボタンを出す。
  // 使えない環境（localhost http / 非対応端末）では false のまま＝カード導線のみ。
  const [walletAvailable, setWalletAvailable] = useState(false)
  // #2190 項目 7: `processing` を見続けている間 / 2 分見ても決まらなかった
  const [waiting, setWaiting] = useState(false)
  const [waitTimedOut, setWaitTimedOut] = useState(false)
  const alive = useRef(true)
  // StrictMode の開発の 2 回目の mount で戻す (cleanup だけだと false のまま見張りが動かない)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  useEffect(() => { onBusyChange?.(waiting && !waitTimedOut) }, [waiting, waitTimedOut, onBusyChange])

  // #2577 予約の仮押さえが切れると、サーバが未入力の決済を取り消して予約を期限切れにする。開いたままの画面から
  // 払おうとすると Stripe は「取り消し済み」で断る。「もう一度お試しください」では同じ決済で失敗し続けるので、申し込み直しへ。
  // ただし、ほかのタブでポイントで払っても決済は取り消される (払う前に閉じる) ので、先に呼び元に確かめてもらう
  const showConfirmError = async (e: StripeError) => {
    if (holdKind === 'reservation' && e.payment_intent?.status === 'canceled') {
      const expired = t('courseReservation.paymentExpired')
      const message = onIntentCanceled ? await onIntentCanceled().catch(() => expired) : expired
      if (message === null) return
      setError(message)
    } else {
      setError(mapStripeError(e, t))
    }
    setIsProcessing(false)
  }

  const watchProcessing = async () => {
    if (!stripe) return
    setWaiting(true)
    setError(null)
    const started = Date.now()
    while (alive.current && Date.now() - started < PROCESSING_WATCH_MS) {
      await new Promise((r) => setTimeout(r, PROCESSING_POLL_MS))
      if (!alive.current) return
      const { paymentIntent, error: readError } = await stripe.retrievePaymentIntent(clientSecret)
      if (readError || !paymentIntent) continue           // 読めないだけ。見続ける
      if (okStatusesFor(captureMethod).includes(paymentIntent.status)) {
        onSuccess(paymentIntent.id)
        return
      }
      if (paymentIntent.status !== 'processing') {
        // 成立しなかった (カードが通らなかった等)。金は動いていないので、もう一度払える
        setWaiting(false)
        setIsProcessing(false)
        setError(t('stripe.genericError'))
        return
      }
    }
    if (alive.current) setWaitTimedOut(true)              // 払い直させない (ボタンは押せないまま)
  }

  // #625: pre-charge fingerprint check が有効になる条件 (props 揃い + 通常 capture)
  // manual-capture は delivery 用で、pre-auth 段階で fingerprint チェックは過剰。
  const usePreCheck = !!paymentIntentId && !!storeId && captureMethod !== 'manual'

  const mapPrecheckErrorMessage = (apiErr: ApiError): string => {
    switch (apiErr.code) {
      case 'PAYMENT_LIMIT_CARD_DAILY':
        return t('payment.limitCardDaily')
      case 'PAYMENT_LIMIT_CARD_RATE':
        return t('payment.limitCardTooFast')
      default:
        return apiErr.message || t('stripe.genericError')
    }
  }

  // #978 カード送信ボタンと Express Checkout(Apple Pay/Google Pay) の両方から呼ぶ
  // 共通の確定処理。elements.submit() → (pre-check) → confirmPayment。
  const runConfirm = async () => {
    if (!stripe || !elements) return
    // #2577 予約の仮押さえの期限を過ぎていたら払わせない (席はもう空いている。巡回が決済を取り消して期限切れにする)。
    // お店がもう承認した予約は仮押さえと関係ないので、呼び元に確かめてもらって払える
    if (payBy && Date.now() > Date.parse(payBy)
        && !(canPayAfterHold && await canPayAfterHold().catch(() => false))) {
      setError(t('courseReservation.paymentExpired'))
      return
    }

    setIsProcessing(true)
    setError(null)

    const { error: submitError } = await elements.submit()
    if (submitError) {
      setError(mapStripeError(submitError, t))
      setIsProcessing(false)
      return
    }

    // #625: pre-charge fingerprint check 経路。
    // PaymentMethod を先に作成 → backend で fingerprint pre-check → 通ったら charge。
    // 違反時は charge を一切走らせないため Stripe 手数料発生ゼロ。
    if (usePreCheck && paymentIntentId && storeId) {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({ elements })
      if (pmError || !paymentMethod) {
        setError(pmError ? mapStripeError(pmError, t) : t('stripe.genericError'))
        setIsProcessing(false)
        return
      }

      try {
        const userId = localStorage.getItem('userId') || ''
        await api.precheckStripePayment(paymentIntentId, storeId, amount, paymentMethod.id, userId)
      } catch (err) {
        if (err instanceof ApiError) {
          setError(mapPrecheckErrorMessage(err))
        } else {
          setError(t('stripe.genericError'))
        }
        setIsProcessing(false)
        return
      }

      // pre-check 通過。実 charge を発火 (3DS 等は Stripe SDK が自動処理)。
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethod.id,
          return_url: window.location.origin + '/payment/complete',
        },
        redirect: 'if_required',
      })

      if (confirmError) {
        const settled = alreadyConfirmedIntentId(confirmError, captureMethod)
        if (settled) {
          onSuccess(settled)
          return
        }
        if (confirmError.payment_intent?.status === 'processing') {
          await watchProcessing()
          return
        }
        await showConfirmError(confirmError)
        return
      }

      if (paymentIntent && okStatusesFor(captureMethod).includes(paymentIntent.status)) {
        onSuccess(paymentIntent.id)
      } else if (paymentIntent?.status === 'processing') {
        await watchProcessing()
      } else {
        setError(t('stripe.genericError'))
        setIsProcessing(false)
      }
      return
    }

    // legacy / manual-capture フロー: PM 作成と confirm を Stripe SDK に任せる。
    // backend は post-charge fallback (#624 race fix + auto-refund) で違反検知する。
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/payment/complete',
      },
      redirect: 'if_required',
    })

    if (confirmError) {
      const settled = alreadyConfirmedIntentId(confirmError, captureMethod)
      if (settled) {
        onSuccess(settled)
        return
      }
      if (confirmError.payment_intent?.status === 'processing') {
        await watchProcessing()
        return
      }
      await showConfirmError(confirmError)
      return
    }

    // For manual-capture (delivery pre-auth), Stripe confirms the PI to
    // `requires_capture` — funds are on hold but not yet captured. Treat
    // that as success from the UI's point of view (store captures later).
    if (paymentIntent && okStatusesFor(captureMethod).includes(paymentIntent.status)) {
      onSuccess(paymentIntent.id)
    } else if (paymentIntent?.status === 'processing') {
      await watchProcessing()
    } else {
      setError(t('stripe.genericError'))
      setIsProcessing(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await runConfirm()
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/10">
          <CreditCard size={20} className="text-navy" />
        </div>
        <div>
          <p className="font-display text-sm font-semibold text-text-primary">
            {t('stripe.payTo', { store: storeName })}
          </p>
          <p className="font-display text-lg font-bold text-text-primary">
            &yen;{amount.toLocaleString()}
          </p>
        </div>
      </div>

      {/* #978 Express Checkout（Apple Pay / Google Pay の大きなボタン）。
        * ウォレットが使える端末でのみ表示（onReady で判定）。タップで runConfirm。
        * localhost(http) や非対応端末では何も描画されない＝カード導線のみ。 */}
      <ExpressCheckoutElement
        onReady={({ availablePaymentMethods }) => setWalletAvailable(!!availablePaymentMethods)}
        onConfirm={runConfirm}
        // #978 Apple Pay / Google Pay を主役に。Link は非表示（スクショの理想 UI に合わせる）。
        options={{ buttonHeight: 48, paymentMethods: { link: 'never' } }}
      />
      {walletAvailable && (
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="font-body text-xs text-text-tertiary">{t('stripe.orPayByCard')}</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
      )}

      {/* Stripe Payment Element — #985 アコーディオン表示。カード等は最初は
        * 折りたたみ行で出し、選択（タップ）で入力欄が展開する。Apple Pay は上の
        * Express Checkout ボタンが主役。 */}
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: true,
            radios: 'always',
            spacedAccordionItems: true,
          },
        }}
      />

      {/* Error message */}
      {error && (
        <p className="font-body text-sm text-red-500">{error}</p>
      )}

      {/* #2190 項目 7: 処理中。払い直させない */}
      {(waiting || waitTimedOut) && (
        <div role="status" className="flex items-start gap-2 rounded-xl bg-gold/10 p-3">
          {!waitTimedOut && <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-gold" />}
          <p className="font-body text-sm text-text-primary">
            {t(waitTimedOut ? 'stripe.processingTimeout' : 'stripe.processingWait')}
          </p>
        </div>
      )}

      {/* Security note */}
      <div className="flex items-center gap-1.5">
        <ShieldCheck size={14} className="text-matcha" />
        <span className="font-body text-[11px] text-text-tertiary">
          {t('stripe.securedByStripe')}
        </span>
      </div>

      {/* Buttons */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className={`tap-effect h-14 w-full rounded-xl font-display text-base font-semibold text-white transition-colors ${
          isProcessing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-yokoso-red shadow-button hover:bg-yokoso-red-dark'
        }`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" />
            {t('stripe.processing')}
          </span>
        ) : (
          captureMethod === 'manual'
            ? t(holdKind === 'experience' || holdKind === 'reservation' ? 'experience.authorizeCta' : 'delivery.authorizeCta',
                { amount: amount.toLocaleString() })
            : t('stripe.pay', { amount: amount.toLocaleString() })
        )}
      </button>

      {captureMethod === 'manual' && (
        <p className="font-body text-[11px] text-text-tertiary leading-relaxed text-center -mt-1">
          {t(holdKind === 'experience' ? 'experience.manualCaptureNote'
            : holdKind === 'reservation' ? 'courseReservation.manualCaptureNote'
              : 'delivery.manualCaptureNote')}
        </p>
      )}

      <button
        type="button"
        onClick={onCancel}
        // #2190 処理中は閉じさせない (閉じて払い直すと二重になる)。2 分見ても決まらなければ閉じられる
        disabled={isProcessing && !waitTimedOut}
        className="tap-effect h-12 w-full rounded-xl border border-gray-200 font-body text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50"
      >
        {t('stripe.cancel')}
      </button>
    </form>
  )
}

export default function StripeCheckout({ clientSecret, amount, storeName, connectedAccountId, paymentIntentId, storeId, captureMethod, holdKind, onSuccess, onCancel, onBusyChange, payBy, canPayAfterHold, onIntentCanceled }: StripeCheckoutProps) {
  // Memoize so every render doesn't create a new loadStripe Promise —
  // <Elements> treats `stripe` as immutable and warns
  // "You cannot change the `stripe` prop after setting it" otherwise.
  const stripePromise = useMemo(
    () => loadStripe(
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
      connectedAccountId ? { stripeAccount: connectedAccountId } : undefined,
    ),
    [connectedAccountId],
  )

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        paymentMethodCreation: 'manual',
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#C0392B',
            borderRadius: '12px',
            fontFamily: '"Inter", "Noto Sans JP", system-ui, sans-serif',
          },
        },
        locale: 'ja',
      }}
    >
      <CheckoutForm
        clientSecret={clientSecret}
        amount={amount}
        storeName={storeName}
        paymentIntentId={paymentIntentId}
        storeId={storeId}
        captureMethod={captureMethod}
        holdKind={holdKind}
        onSuccess={onSuccess}
        onCancel={onCancel}
        onBusyChange={onBusyChange}
        payBy={payBy}
        canPayAfterHold={canPayAfterHold}
        onIntentCanceled={onIntentCanceled}
      />
    </Elements>
  )
}
